import { useCallback, useEffect } from "react";

import { useLibrary } from "../../../../providers";
import { useObjectMemo } from "../../../../hooks";
import { centsToDollars } from "../../../../utils";

import { customAlertForm } from "./form";

import type { CustomAlertPayload } from "./types";

const DESCRIPTION_DELIMITER = ",";

export const useCustomAlertForm = () => {
  const form = customAlertForm.useForm();
  const { submitForm, resetForm, values } = form;

  const onValidate = useCallback(async () => {
    const result = await submitForm();
    return result.success;
  }, [submitForm]);

  const constructQuery = useCallback((): string => {
    const queryParts: string[] = [];

    const {
      greaterThanAmount,
      lessThanAmount,
      greaterThanBalance,
      lessThanBalance,
      accounts,
      description,
    } = form.values;

    if (accounts !== null && accounts.length === 1) {
      queryParts.push(`account:${accounts[0]}`);
    }

    if (greaterThanAmount !== null) {
      queryParts.push(`amount>${centsToDollars(greaterThanAmount)}`);
    }

    if (lessThanAmount !== null) {
      queryParts.push(`amount<${centsToDollars(lessThanAmount)}`);
    }

    if (greaterThanBalance !== null) {
      queryParts.push(`balance>${centsToDollars(greaterThanBalance)}`);
    }

    if (lessThanBalance !== null) {
      queryParts.push(`balance<${centsToDollars(lessThanBalance)}`);
    }

    if (description) {
      description
        .replace(/[\n]+/g, " ")
        .split(DESCRIPTION_DELIMITER)
        .forEach((word) => {
          if (word.trim().length > 0) {
            queryParts.push(word.trim());
          }
        });
    }

    return !queryParts.length ? "" : queryParts.join(" ");
  }, [form.values]);

  const createRequestPayload = useCallback(() => {
    return {
      model: form.values.model,
      alert_type: form.values.deliveryMethod,
      query: constructQuery(),
    } as CustomAlertPayload;
  }, [constructQuery, form.values.deliveryMethod, form.values.model]);

  useEffect(() => {
    return resetForm;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useObjectMemo({
    form,
    values,
    onValidate,
    resetForm,
    createRequestPayload,
  });
};

// For debugging purposes
export const useFormErrors = () => {
  const form = customAlertForm.useForm();
  const { throwToast } = useLibrary("toasts");

  const throwError = useCallback(
    (message: string | string[]) => {
      throwToast({ kind: "error", message: `form error: ${message}` });
    },
    [throwToast],
  );

  useEffect(() => {
    if (form.errors) {
      Object.keys(form.errors).forEach((errorKey) => {
        const error = (form.errors as any)[errorKey];
        if (error) throwError(error);
      });
    }
  }, [form.errors, throwError]);
};
